import { useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  authenticateUser,
  invalidateSession,
  clearAuthError,
  selectUser,
  selectIsAuthenticated,
  selectIsLoading,
  selectIsRefreshing,
  selectAuthError,
  selectIsInitialized,
  selectAccessToken,
  selectRefreshToken,
  selectUserDisplayName,
  selectHasRefreshToken,
  selectIsOperationInProgress,
} from '../store/slices/authSlice';

/**
 * @typedef {Object} AuthCredentials
 * @property {string} emailAddress - User's email address
 * @property {string} password - User's password
 */

/**
 * @typedef {Object} AuthOperationResult
 * @property {boolean} success - Operation success status
 * @property {Object} [user] - User data (on success)
 * @property {string} [error] - Error message (on failure)
 * @property {string} [message] - Success message
 */

/**
 * @typedef {Object} UserProfile
 * @property {string} id - User ID
 * @property {string} email - User email
 * @property {string} firstName - User first name
 * @property {string} lastName - User last name
 * @property {string} name - Full name
 * @property {string} role - User role
 * @property {string} companyId - Company ID
 */

/**
 * Authentication Hook
 * 
 * Features:
 * - Proper hooks usage (all hooks at top level)
 * - Memoized selectors for performance
 * - Stable callback references
 * - Comprehensive error handling
 * - Type-safe operations
 * - Domain-specific utility functions
 * 
 * @returns {Object} Authentication state and operations
 */
export const useAuth = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const isRefreshing = useSelector(selectIsRefreshing);
  const error = useSelector(selectAuthError);
  const isInitialized = useSelector(selectIsInitialized);
  const accessToken = useSelector(selectAccessToken);
  const refreshToken = useSelector(selectRefreshToken);
  const userDisplayName = useSelector(selectUserDisplayName);
  const hasRefreshToken = useSelector(selectHasRefreshToken);
  const isOperationInProgress = useSelector(selectIsOperationInProgress);

  const authState = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    isRefreshing,
    error,
    isInitialized,
    accessToken,
    refreshToken,
    userDisplayName,
    hasRefreshToken,
    isOperationInProgress,
  }), [
    user,
    isAuthenticated,
    isLoading,
    isRefreshing,
    error,
    isInitialized,
    accessToken,
    refreshToken,
    userDisplayName,
    hasRefreshToken,
    isOperationInProgress,
  ]);

  // refreshPermissions çağrısını kaldırdık çünkü bu gereksiz tetiklemelere neden oluyor
  // Permissions zaten usePermissions hook'unda otomatik olarak yükleniyor

  /**
   * Authenticate user with credentials
   * @param {string} emailAddress - User's email
   * @param {string} password - User's password
   * @returns {Promise<AuthOperationResult>} Authentication result
   */
  const login = useCallback(async (emailAddress, password) => {
    try {
      dispatch(clearAuthError());

      const credentials = { emailAddress, password };
      const result = await dispatch(authenticateUser(credentials));

      if (result.type === 'auth/authenticate/fulfilled') {
        return {
          success: true,
          user: result.payload.user
        };
      } else {
        return {
          success: false,
          error: result.payload || 'Authentication failed'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Unexpected login error'
      };
    }
  }, [dispatch]);

  /**
   * Invalidate user session (logout)
   * @returns {Promise<AuthOperationResult>} Logout result
   */
  const logout = useCallback(async () => {
    try {
      const result = await dispatch(invalidateSession());

      return {
        success: true,
        message: result.payload?.message || 'Successfully logged out'
      };
    } catch (error) {
      return {
        success: true,
        message: 'Logged out'
      };
    }
  }, [dispatch]);

  /**
   * Clear authentication errors
   */
  const clearError = useCallback(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  // Domain-specific utility functions

  /**
   * Check if user has specific role(s)
   * @param {string|string[]} roles - Role(s) to check
   * @returns {boolean} Whether user has the role(s)
   */
  const hasRole = useCallback((roles) => {
    if (!user?.role) return false;

    const userRole = user.role;
    
    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }
    
    return userRole === roles;
  }, [user?.role]);

  const computed = useMemo(() => ({
    canRefreshToken: hasRefreshToken && !isRefreshing,
    isReady: isInitialized && !isLoading,
    needsAuthentication: isInitialized && !isAuthenticated,
  }), [hasRefreshToken, isRefreshing, isInitialized, isLoading, isAuthenticated]);

  return useMemo(() => ({
    // State
    ...authState,
    
    // Computed state
    ...computed,

    // Core operations
    login,
    logout,
    clearError,

    // Authorization utilities
    hasRole,
  }), [
    authState,
    computed,
    login,
    logout,
    clearError,
    hasRole,
  ]);
};

/**
 * Legacy export for backward compatibility
 * @deprecated Use named export { useAuth } instead
 */
export default useAuth;
